<template>
  <div class="telemetry-status-chart">
    <vue-apex-charts
      type="donut"
      height="320"
      :options="chartOptions"
      :series="normalizedSeries"
    />
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

const formatChartLabel = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(number)
}

const chartColors = () => {
  const dark = document.body.classList.contains('dark-layout')
  return {
    label: dark ? '#b4b7bd' : '#6e6b7b',
    value: dark ? '#d0d2d6' : '#5e5873',
    stroke: dark ? '#283046' : '#ffffff',
  }
}

export default {
  components: {
    VueApexCharts,
  },
  props: {
    labels: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
  },
  computed: {
    normalizedSeries() {
      return this.values.map(value => Number(value) || 0)
    },
    chartOptions() {
      const colors = chartColors()
      const totalLabel = this.$t('Total')
      return {
        chart: {
          type: 'donut',
          toolbar: { show: false },
          fontFamily: 'Montserrat, Helvetica, Arial, serif',
          parentHeightOffset: 0,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 450,
          },
        },
        labels: this.labels,
        colors: ['#28c76f', '#ff9f43', '#ea5455'],
        stroke: {
          width: 3,
          colors: [colors.stroke],
        },
        dataLabels: {
          enabled: true,
          formatter: val => (val < 4 ? '' : `${formatChartLabel(val)}%`),
          style: {
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: 'Montserrat, Helvetica, Arial, serif',
          },
          dropShadow: { enabled: false },
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '13px',
          fontWeight: 500,
          markers: { width: 10, height: 10, radius: 12 },
          itemMargin: { horizontal: 12, vertical: 6 },
          labels: { colors: colors.label },
          formatter: (seriesName, opts) => {
            const value = opts?.w?.globals?.series?.[opts.seriesIndex]
            return `${seriesName}: ${formatChartLabel(value)}%`
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: true,
            donut: {
              size: '68%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.value,
                  offsetY: -8,
                },
                value: {
                  show: true,
                  fontSize: '22px',
                  fontWeight: 700,
                  color: colors.value,
                  offsetY: 6,
                  formatter: val => `${formatChartLabel(val)}%`,
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: totalLabel,
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.label,
                  formatter: (w) => {
                    const sum = (w?.globals?.seriesTotals || []).reduce((total, value) => total + Number(value || 0), 0)
                    return `${formatChartLabel(sum)}%`
                  },
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: value => `${formatChartLabel(value)}%`,
          },
        },
        responsive: [{
          breakpoint: 576,
          options: {
            chart: { height: 300 },
            legend: { position: 'bottom' },
          },
        }],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.telemetry-status-chart {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}
</style>
